import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import type { RefreshLinkUrlHandler } from '../connectionTypes.ts';
import { useConnectSmartLinkAccount } from '../hooks/useConnectSmartLinkAccount.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

const i18n = defineMessages({
	connect: {
		id: 'byline-connections.menu.item.connect',
		defaultMessage: 'Connect',
		description:
			'Action label shown on the right of an unconnected connections menu item, prompting the user to connect the related product.',
	},
	connecting: {
		id: 'byline-connections.menu.item.connecting',
		defaultMessage: 'Connecting...',
		description:
			'Status label shown on the right of an unconnected connections menu item while the related product account is being connected.',
	},
});

const styles = cssMap({
	trailing: {
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
	},
});

const pressableStyles = cssMap({
	connect: {
		font: token('font.body'),
		color: token('color.link'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		cursor: 'pointer',
		paddingBlock: token('space.0'),
		paddingInline: token('space.050'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	connecting: {
		font: token('font.body'),
		color: token('color.text.disabled'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		cursor: 'default',
		paddingBlock: token('space.0'),
		paddingInline: token('space.050'),
		textDecoration: 'none',
	},
});

export const ConnectionRowConnectButton = ({
	item,
	refreshLinkUrl,
	onConnectSuccess,
}: {
	item: EnhancedSmartLink;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onConnectSuccess?: () => void;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { connect, isConnecting } = useConnectSmartLinkAccount({
		item,
		refreshLinkUrl,
		onConnectSuccess,
	});

	return (
		<Box xcss={styles.trailing}>
			<Pressable
				xcss={isConnecting ? pressableStyles.connecting : pressableStyles.connect}
				onClick={connect}
				isDisabled={isConnecting}
			>
				{formatMessage(isConnecting ? i18n.connecting : i18n.connect)}
			</Pressable>
		</Box>
	);
};
